import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { updateProject } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { Pipeline, calcPct, calcLatestEndDate, getMilestonePct, getMilestoneStartDate } from './CustomerUpdateDisplay'

const DEPT_HEX = {
  MX: '#22c55e', AVI: '#3b82f6', INT: '#f97316',
  PA: '#ec4899', QA: '#eab308',
}
const DEPT_LABELS = {
  MX: 'Maintenance', AVI: 'Avionics', INT: 'Interior',
  PA: 'Paint', QA: 'Quality Assurance',
}

function uid() { return crypto.randomUUID() }

function fmtDate(iso) {
  if (!iso) return ''
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtDateShort(iso) {
  if (!iso) return ''
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function getMilestoneDate(m, allSubtasks) {
  if (m.milestone_date) return m.milestone_date
  return calcLatestEndDate(m.subtask_ids || [], allSubtasks)
}

function syncMilestoneTable(data) {
  const all = data.dept_rows.flatMap(r => r.milestones)
  const allIds = new Set(all.map(m => m.id))
  const deleted = new Set(data.milestone_table.deleted_auto_ids || [])
  const existing = data.milestone_table.rows.filter(r => r.is_manual || allIds.has(r.id))
  const existIds = new Set(existing.map(r => r.id))
  const added = all
    .filter(m => !existIds.has(m.id) && !deleted.has(m.id))
    .map(m => ({ id: m.id, milestone: m.name, target_date: '', is_manual: false, subtask_ids: m.subtask_ids || [] }))
  const updatedRows = existing.map(r => {
    if (r.is_manual) return r
    const m = all.find(x => x.id === r.id)
    if (!m) return r
    const subtask_ids = r.subtask_ids?.length > 0 ? r.subtask_ids : (m.subtask_ids || [])
    return { ...r, milestone: m.name, subtask_ids }
  })
  const finalRows = [...updatedRows, ...added]
  const unchanged =
    finalRows.length === data.milestone_table.rows.length &&
    finalRows.every((r, i) => {
      const o = data.milestone_table.rows[i]
      return r.id === o.id && r.milestone === o.milestone && r.is_manual === o.is_manual
    })
  if (unchanged) return data
  return { ...data, milestone_table: { ...data.milestone_table, rows: finalRows } }
}

// ── Subtask Selector ──────────────────────────────────────────────────────────

function SubtaskSelector({ dept, selectedIds, allSubtasks, onChange }) {
  const [open, setOpen] = useState(false)
  const [completeExpanded, setCompleteExpanded] = useState(false)
  const ref = useRef(null)
  const subtaskDepts = s => s.departments?.length > 0 ? s.departments : [s.department]
  const deptSubtasks = dept
    ? allSubtasks.filter(s => s.task_name && subtaskDepts(s).includes(dept))
    : allSubtasks.filter(s => s.task_name)

  useEffect(() => {
    if (!open) return
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  const toggle = (id) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id])

  const byDate = (a, b) => (a.start_date || '').localeCompare(b.start_date || '')
  const groups = [
    { key: 'Complete',  label: 'Complete',  items: deptSubtasks.filter(s => s.status === 'Complete').sort(byDate),  collapsible: true  },
    { key: 'In Work',   label: 'In Work',   items: deptSubtasks.filter(s => s.status === 'In Work').sort(byDate),   collapsible: false },
    { key: 'Scheduled', label: 'Scheduled', items: deptSubtasks.filter(s => s.status === 'Scheduled').sort(byDate), collapsible: false },
  ].filter(g => g.items.length > 0)

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg px-2 py-1 bg-white hover:bg-gray-50 w-full"
      >
        <span className="flex-1 text-left">
          {selectedIds.length ? `${selectedIds.length} subtask${selectedIds.length !== 1 ? 's' : ''} linked` : 'Link subtasks…'}
        </span>
        <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-[9999] top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          {deptSubtasks.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-400">No {dept} subtasks on this project</div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              {groups.map(group => {
                const isCollapsed = group.collapsible && !completeExpanded
                return (
                  <div key={group.key}>
                    <button
                      type="button"
                      onClick={() => group.collapsible && setCompleteExpanded(v => !v)}
                      className={`w-full flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-100 ${group.collapsible ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'}`}
                    >
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{group.label} ({group.items.length})</span>
                      {group.collapsible && (
                        <svg className={`w-3 h-3 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </button>
                    {!isCollapsed && (
                      <div className="divide-y divide-gray-50">
                        {group.items.map(s => (
                          <label key={s.id} className="flex items-start gap-2.5 px-3 py-2 hover:bg-brand-red/10 cursor-pointer">
                            <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => toggle(s.id)} className="mt-0.5 rounded" />
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium text-gray-700 truncate">{s.task_name}</div>
                              {s.start_date && s.end_date && (
                                <div className="text-xs text-gray-400">{fmtDate(s.start_date)} – {fmtDate(s.end_date)}</div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 flex justify-between">
            <button type="button" onClick={() => onChange(deptSubtasks.map(s => s.id))} className="text-xs text-brand-red hover:text-brand-red-dark font-medium">Select all</button>
            <button type="button" onClick={() => onChange([])} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Editor Milestone Row ──────────────────────────────────────────────────────

function EditorMilestoneRow({ milestone, dept, deptColor, allSubtasks, index, total, onChange, onDelete, onMove }) {
  const [openEdit, setOpenEdit] = useState(false)
  const popRef = useRef(null)
  const isDateMode = !!milestone.milestone_date
  const isSubtaskDateMode = !isDateMode && !!milestone.show_subtask_date

  useEffect(() => {
    if (!openEdit) return
    const h = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpenEdit(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [openEdit])

  const pct        = getMilestonePct(milestone, allSubtasks)
  const isComplete = pct >= 100
  const isActive   = pct > 0 && pct < 100
  const startDate  = getMilestoneStartDate(milestone, allSubtasks)
  const endDate    = getMilestoneDate(milestone, allSubtasks)
  const autoPct    = calcPct(milestone.subtask_ids || [], allSubtasks)
  const hasOverride = milestone.pct_override !== null && milestone.pct_override !== undefined

  const switchToDate        = () => onChange({ ...milestone, milestone_date: new Date().toISOString().slice(0, 10), subtask_ids: [], pct_override: null, show_subtask_date: false })
  const switchToPct         = () => onChange({ ...milestone, milestone_date: null, show_subtask_date: false })
  const switchToSubtaskDate = () => onChange({ ...milestone, milestone_date: null, show_subtask_date: true, pct_override: null })

  return (
    <div className="relative flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50">
      {/* Status icon */}
      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
        {isComplete ? (
          <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        ) : isActive ? (
          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${deptColor}20` }}>
            <svg className="w-2.5 h-2.5" fill={deptColor} viewBox="0 0 24 24">
              <polygon points="7,4 20,12 7,20" />
            </svg>
          </div>
        ) : (
          <div className="w-5 h-5 rounded-full border-2 border-gray-200" />
        )}
      </div>

      {/* Name */}
      <span
        className="flex-1 text-sm truncate"
        style={{ color: isComplete ? '#9ca3af' : '#111827', fontWeight: isComplete ? 400 : 500 }}
      >
        {milestone.name || <span className="text-gray-300 italic">Unnamed</span>}
      </span>

      {/* % Complete — bar + number combined */}
      <div className="flex items-center gap-1.5 flex-shrink-0 w-32 justify-end">
        {!isDateMode && !isSubtaskDateMode && (
          <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-gray-100">
            <div style={{ width: `${pct}%`, height: '100%', backgroundColor: isComplete ? '#d1d5db' : deptColor, borderRadius: 9999, transition: 'width 0.4s ease' }} />
          </div>
        )}
        <span className="text-xs tabular-nums flex-shrink-0 w-9 text-right font-semibold" style={{ color: isComplete ? '#9ca3af' : '#6b7280' }}>
          {(!isDateMode && !isSubtaskDateMode) ? `${pct}%` : '—'}
        </span>
      </div>

      {/* Start Date */}
      <span className="text-xs text-gray-400 flex-shrink-0 w-20 text-right">
        {startDate ? fmtDateShort(startDate) : '—'}
      </span>

      {/* End Date */}
      <span className="text-xs text-gray-400 flex-shrink-0 w-20 text-right">
        {endDate ? fmtDateShort(endDate) : '—'}
      </span>

      {/* Editor controls */}
      <div className="no-print flex items-center gap-0.5 flex-shrink-0">
        <button
          onClick={() => setOpenEdit(o => !o)}
          title="Edit milestone"
          className={`w-6 h-6 flex items-center justify-center rounded-md transition-colors ${openEdit ? 'bg-brand-red/10 text-brand-red' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <button onClick={() => onMove(-1)} disabled={index === 0}
          className="w-6 h-6 flex items-center justify-center rounded-md text-gray-300 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <button onClick={() => onMove(1)} disabled={index === total - 1}
          className="w-6 h-6 flex items-center justify-center rounded-md text-gray-300 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <button onClick={onDelete}
          className="w-6 h-6 flex items-center justify-center rounded-md text-gray-300 hover:text-red-400 transition-colors">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Edit popover */}
      {openEdit && (
        <div
          ref={popRef}
          className="no-print absolute z-[9999] right-16 top-full mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-2xl p-3 space-y-2.5"
          onClick={e => e.stopPropagation()}
        >
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Name</label>
            <input
              autoFocus
              value={milestone.name}
              onChange={e => onChange({ ...milestone, name: e.target.value })}
              className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-red"
              placeholder="Milestone name…"
            />
          </div>

          <div className="flex gap-1.5">
            <button onClick={switchToPct} className={`flex-1 text-xs py-1 rounded-lg border font-medium transition-colors ${!isDateMode && !isSubtaskDateMode ? 'bg-brand-red text-white border-brand-red' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
              % Progress
            </button>
            <button onClick={switchToSubtaskDate} className={`flex-1 text-xs py-1 rounded-lg border font-medium transition-colors ${isSubtaskDateMode ? 'bg-brand-red text-white border-brand-red' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
              Subtask Date
            </button>
            <button onClick={switchToDate} className={`flex-1 text-xs py-1 rounded-lg border font-medium transition-colors ${isDateMode ? 'bg-brand-red text-white border-brand-red' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
              Set Date
            </button>
          </div>

          {isDateMode ? (
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Milestone Date</label>
              <input
                type="date"
                value={milestone.milestone_date || ''}
                onChange={e => onChange({ ...milestone, milestone_date: e.target.value })}
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-red"
              />
            </div>
          ) : isSubtaskDateMode ? (
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Linked Subtasks</label>
              <SubtaskSelector dept={dept} selectedIds={milestone.subtask_ids || []} allSubtasks={allSubtasks} onChange={ids => onChange({ ...milestone, subtask_ids: ids })} />
              {endDate && <p className="text-xs text-gray-400 mt-1.5">Shows: {fmtDateShort(endDate)}</p>}
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Linked Subtasks</label>
                <SubtaskSelector dept={dept} selectedIds={milestone.subtask_ids || []} allSubtasks={allSubtasks} onChange={ids => onChange({ ...milestone, subtask_ids: ids })} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">
                  % Override {autoPct !== null && !hasOverride && <span className="text-gray-300 normal-case font-normal">(auto: {autoPct}%)</span>}
                </label>
                {hasOverride ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" max="100"
                      value={milestone.pct_override}
                      onChange={e => onChange({ ...milestone, pct_override: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                      className="w-16 text-sm border border-brand-red/30 rounded-lg px-2 py-1 text-center focus:outline-none"
                    />
                    <button onClick={() => onChange({ ...milestone, pct_override: null })} className="text-xs text-brand-red hover:text-brand-red-dark">Use auto</button>
                  </div>
                ) : (
                  <button onClick={() => onChange({ ...milestone, pct_override: autoPct ?? 0 })} className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg px-2.5 py-1 w-full text-left">
                    Override manually…
                  </button>
                )}
              </div>
            </>
          )}

          <button onClick={() => { setOpenEdit(false); onDelete() }} className="w-full text-xs text-red-500 hover:text-red-700 hover:bg-brand-red/10 border border-red-200 rounded-lg py-1.5 transition-colors">
            Remove milestone
          </button>
        </div>
      )}
    </div>
  )
}

// ── Editor Dept Row ───────────────────────────────────────────────────────────

function EditorDeptRow({ deptRow, orgDeptMap, index, total, allSubtasks, onMove, onChange, onDelete }) {
  const { dept, milestones } = deptRow
  const color = orgDeptMap[dept]?.hex || DEPT_HEX[dept] || '#6b7280'
  const label = orgDeptMap[dept]?.label || DEPT_LABELS[dept] || dept
  const deptPct = milestones.length
    ? Math.round(milestones.reduce((s, m) => s + getMilestonePct(m, allSubtasks), 0) / milestones.length)
    : 0

  const addMilestone = () =>
    onChange({ ...deptRow, milestones: [...milestones, { id: uid(), name: '', subtask_ids: [], pct_override: null, milestone_date: null }] })

  const moveMilestone = (idx, dir) => {
    const ni = idx + dir
    if (ni < 0 || ni >= milestones.length) return
    const ms = [...milestones]
    ;[ms[idx], ms[ni]] = [ms[ni], ms[idx]]
    onChange({ ...deptRow, milestones: ms })
  }

  return (
    <div className="mb-3 last:mb-0">
      {/* Dept header */}
      <div
        className="flex items-center gap-3 px-4 py-2.5 bg-gray-50/80 rounded-t-xl border border-gray-200 border-b-0"
        style={{ borderLeftWidth: 3, borderLeftColor: color }}
      >
        <div className="no-print flex gap-0.5 flex-shrink-0">
          <button onClick={() => onMove(index, -1)} disabled={index === 0}
            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-300 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button onClick={() => onMove(index, 1)} disabled={index === total - 1}
            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-300 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <span className="text-xs font-bold tracking-widest uppercase text-gray-700">{label}</span>
        <div className="flex-1" />
        <span className="text-xs font-semibold text-gray-500 tabular-nums">{deptPct}%</span>
        <div className="w-20 h-1.5 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
          <div style={{ width: `${deptPct}%`, height: '100%', backgroundColor: color, borderRadius: 9999, transition: 'width 0.4s ease' }} />
        </div>

        <div className="no-print flex items-center gap-2">
          <button onClick={addMilestone} className="flex items-center gap-1 text-xs text-brand-red hover:text-brand-red-dark font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Milestone
          </button>
          <button onClick={onDelete} className="text-gray-300 hover:text-red-400 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Milestone list */}
      <div className="border border-gray-200 rounded-b-xl overflow-visible">
        {milestones.length === 0 ? (
          <div className="px-4 py-3 text-xs text-gray-400 italic">No milestones — click "+ Milestone" to add one.</div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200">
              <div className="flex-shrink-0 w-5" />
              <span className="flex-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Task</span>
              <span className="w-32 text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">% Complete</span>
              <span className="w-20 text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Start Date</span>
              <span className="w-20 text-xs font-semibold text-gray-400 uppercase tracking-wide text-right no-print">End Date</span>
              <div className="no-print flex items-center gap-0.5 flex-shrink-0">
                <div className="w-6" /><div className="w-6" /><div className="w-6" /><div className="w-6" />
              </div>
            </div>
          <div className="divide-y divide-gray-100">
            {milestones.map((m, idx) => (
              <EditorMilestoneRow
                key={m.id}
                milestone={m}
                dept={dept}
                deptColor={color}
                allSubtasks={allSubtasks}
                index={idx}
                total={milestones.length}
                onChange={u => onChange({ ...deptRow, milestones: milestones.map((x, j) => j === idx ? u : x) })}
                onDelete={() => onChange({ ...deptRow, milestones: milestones.filter((_, j) => j !== idx) })}
                onMove={dir => moveMilestone(idx, dir)}
              />
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Milestone Table Row ───────────────────────────────────────────────────────

function MilestoneTableRow({ row, rowIdx, totalRows, allSubtasks, onSetField, onMove, onDelete, isEditor }) {
  const [linkOpen, setLinkOpen] = useState(false)
  const [dropPos, setDropPos] = useState({})
  const [completeExpanded, setCompleteExpanded] = useState(false)
  const buttonRef = useRef(null)
  const dropRef = useRef(null)

  useEffect(() => {
    if (!linkOpen) return
    const h = (e) => {
      if (buttonRef.current?.contains(e.target)) return
      if (dropRef.current?.contains(e.target)) return
      setLinkOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [linkOpen])

  const openDropdown = () => {
    if (linkOpen) { setLinkOpen(false); return }
    const rect = buttonRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    setDropPos(spaceBelow < 240
      ? { bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right }
      : { top: rect.bottom + 4, right: window.innerWidth - rect.right }
    )
    setLinkOpen(true)
  }

  const linkedIds = row.subtask_ids || []
  const computedDate = calcLatestEndDate(linkedIds, allSubtasks)
  const displayDate = row.target_date || computedDate || ''
  const namedSubtasks = allSubtasks.filter(s => s.task_name)

  const toggleSubtask = (id) =>
    onSetField('subtask_ids', linkedIds.includes(id) ? linkedIds.filter(x => x !== id) : [...linkedIds, id])

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50">
      {row.is_manual && isEditor ? (
        <input
          value={row.milestone}
          onChange={e => onSetField('milestone', e.target.value)}
          placeholder="Milestone name…"
          className="flex-1 text-sm text-gray-800 bg-transparent focus:outline-none placeholder-gray-300 border-b border-transparent focus:border-gray-300"
        />
      ) : (
        <span className="flex-1 text-sm text-gray-800 truncate">{row.milestone || <span className="text-gray-300 italic">Unnamed</span>}</span>
      )}

      {isEditor && (
        <div className="no-print flex-shrink-0">
          <button
            ref={buttonRef}
            type="button"
            onClick={openDropdown}
            title="Link subtasks to auto-fill date"
            className={`flex items-center gap-1 text-xs rounded-lg px-1.5 py-0.5 border transition-colors ${linkedIds.length > 0 ? 'border-brand-red/30 text-brand-red bg-brand-red/10' : 'border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300'}`}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {linkedIds.length > 0 && <span>{linkedIds.length}</span>}
          </button>
          {linkOpen && (
            <div
              ref={dropRef}
              style={{ position: 'fixed', zIndex: 9999, width: 256, ...dropPos }}
              className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
            >
              {namedSubtasks.length === 0 ? (
                <div className="px-4 py-3 text-xs text-gray-400">No subtasks on this project</div>
              ) : (() => {
                const byDate = (a, b) => (a.start_date || '').localeCompare(b.start_date || '')
                const groups = [
                  { key: 'Complete',  label: 'Complete',  items: namedSubtasks.filter(s => s.status === 'Complete').sort(byDate),  collapsible: true  },
                  { key: 'In Work',   label: 'In Work',   items: namedSubtasks.filter(s => s.status === 'In Work').sort(byDate),   collapsible: false },
                  { key: 'Scheduled', label: 'Scheduled', items: namedSubtasks.filter(s => s.status === 'Scheduled').sort(byDate), collapsible: false },
                ].filter(g => g.items.length > 0)
                return (
                  <div className="max-h-64 overflow-y-auto">
                    {groups.map(group => {
                      const isCollapsed = group.collapsible && !completeExpanded
                      return (
                        <div key={group.key}>
                          <button
                            type="button"
                            onClick={() => group.collapsible && setCompleteExpanded(v => !v)}
                            className={`w-full flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-100 ${group.collapsible ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'}`}
                          >
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{group.label} ({group.items.length})</span>
                            {group.collapsible && (
                              <svg className={`w-3 h-3 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                          </button>
                          {!isCollapsed && (
                            <div className="divide-y divide-gray-50">
                              {group.items.map(s => (
                                <label key={s.id} className="flex items-start gap-2.5 px-3 py-2 hover:bg-brand-red/10 cursor-pointer">
                                  <input type="checkbox" checked={linkedIds.includes(s.id)} onChange={() => toggleSubtask(s.id)} className="mt-0.5 rounded" />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-gray-700 truncate">{s.task_name}</div>
                                    {s.start_date && s.end_date && (
                                      <div className="text-xs text-gray-400">{fmtDate(s.start_date)} – {fmtDate(s.end_date)}</div>
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
              <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 flex justify-between">
                <button type="button" onClick={() => onSetField('subtask_ids', namedSubtasks.map(s => s.id))} className="text-xs text-brand-red hover:text-brand-red-dark font-medium">Select all</button>
                <button type="button" onClick={() => onSetField('subtask_ids', [])} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
              </div>
            </div>
          )}
        </div>
      )}

      {isEditor && (
        <input
          type="date"
          value={displayDate}
          onChange={e => onSetField('target_date', e.target.value)}
          title={!row.target_date && computedDate ? `Auto-filled from linked subtasks — enter a date to override` : ''}
          className="no-print w-28 text-xs text-right text-gray-600 bg-transparent border border-gray-200 rounded-lg px-2 py-0.5 focus:outline-none focus:border-brand-red"
        />
      )}

      <span className={`text-xs text-right w-28 text-gray-600 ${isEditor ? 'hidden print:block' : 'block'}`}>
        {fmtDate(displayDate) || '—'}
      </span>

      {isEditor && (
        <div className="no-print flex gap-0.5 flex-shrink-0">
          <button onClick={() => onMove(-1)} disabled={rowIdx === 0}
            className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button onClick={() => onMove(1)} disabled={rowIdx === totalRows - 1}
            className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button onClick={onDelete}
            className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-red-400 transition-colors">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function CustomerUpdatePanel({ projectId, project, subtasks = [], isEditor }) {
  const buildEmpty = useCallback(() => ({
    header: {
      registration: project?.aircraft?.registration || project?.aircraft_registration || '',
      model: project?.aircraft?.model || '',
      customer: project?.customer_name || '',
      work_order: project?.work_order || '',
      report_date: new Date().toISOString().slice(0, 10),
    },
    dept_rows: [],
    milestone_table: { rows: [], deleted_auto_ids: [] },
    overall_pct_override: null,
  }), [project])

  const auth = useAuth() ?? {}
  const { profile } = auth
  const orgLogoUrl = profile?.orgs?.settings?.logo_url ?? '/logo.jpg'
  const orgDepts = profile?.orgs?.settings?.departments ?? []
  const orgDeptMap = Object.fromEntries(orgDepts.map(d => [d.key, d]))

  const [data, setData] = useState(buildEmpty)
  const serverLoaded = useRef(false)
  const dataRef = useRef(data)
  dataRef.current = data
  const saveTimer = useRef(null)
  const projectIdRef = useRef(projectId)
  projectIdRef.current = projectId

  useEffect(() => {
    serverLoaded.current = false
  }, [projectId])

  useEffect(() => {
    if (serverLoaded.current || !project || project.id !== projectId) return
    serverLoaded.current = true
    const base = project.customer_update != null ? project.customer_update : buildEmpty()
    setData({
      ...base,
      header: {
        ...base.header,
        work_order: project.work_order || base.header?.work_order || '',
      },
    })
  }, [project, buildEmpty, projectId])

  // Debounced auto-save — only runs for editors; no cleanup so pending saves
  // fire even if the component unmounts before the 800 ms window closes
  useEffect(() => {
    if (!isEditor) return
    if (!serverLoaded.current) return
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      const { error } = await updateProject(projectIdRef.current, { customer_update: dataRef.current })
      if (error) console.error('[customer_update save]', error)
    }, 800)
  }, [data, isEditor])

  const update = (updater) => {
    setData(prev => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }
      return syncMilestoneTable(next)
    })
  }

  const setHeader = (field, val) => update(d => ({ ...d, header: { ...d.header, [field]: val } }))

  const addDeptRow = (dept) =>
    update(d => ({ ...d, dept_rows: [...d.dept_rows, { id: uid(), dept, milestones: [] }] }))

  const moveDeptRow = (idx, dir) => {
    update(d => {
      const rows = [...d.dept_rows]
      const ni = idx + dir
      if (ni < 0 || ni >= rows.length) return d
      ;[rows[idx], rows[ni]] = [rows[ni], rows[idx]]
      return { ...d, dept_rows: rows }
    })
  }

  const updateDeptRow = (idx, row) =>
    update(d => ({ ...d, dept_rows: d.dept_rows.map((r, i) => i === idx ? row : r) }))

  const deleteDeptRow = (idx) =>
    update(d => ({ ...d, dept_rows: d.dept_rows.filter((_, i) => i !== idx) }))

  const setTableRow = (id, field, val) =>
    setData(d => ({
      ...d,
      milestone_table: { ...d.milestone_table, rows: d.milestone_table.rows.map(r => r.id === id ? { ...r, [field]: val } : r) },
    }))

  const addManualRow = () =>
    setData(d => ({
      ...d,
      milestone_table: { ...d.milestone_table, rows: [...d.milestone_table.rows, { id: uid(), milestone: '', target_date: '', is_manual: true, subtask_ids: [] }] },
    }))

  const deleteTableRow = (id, isManual) =>
    setData(d => ({
      ...d,
      milestone_table: {
        rows: d.milestone_table.rows.filter(r => r.id !== id),
        deleted_auto_ids: isManual ? (d.milestone_table.deleted_auto_ids || []) : [...(d.milestone_table.deleted_auto_ids || []), id],
      },
    }))

  const moveTableRow = (idx, dir) =>
    setData(d => {
      const rows = [...d.milestone_table.rows]
      const ni = idx + dir
      if (ni < 0 || ni >= rows.length) return d
      ;[rows[idx], rows[ni]] = [rows[ni], rows[idx]]
      return { ...d, milestone_table: { ...d.milestone_table, rows } }
    })

  const overallPct = useMemo(() => {
    if (data.overall_pct_override !== null && data.overall_pct_override !== undefined) return data.overall_pct_override
    if (!project?.arrival_date || !project?.departure_date) return 0
    const start = new Date(project.arrival_date + 'T00:00:00')
    const end = new Date(project.departure_date + 'T00:00:00')
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const total = (end - start) / 86400000
    if (total <= 0) return today >= end ? 100 : 0
    return Math.round(Math.min(Math.max((today - start) / 86400000, 0), total) / total * 100)
  }, [data.overall_pct_override, project?.arrival_date, project?.departure_date])

  const handlePrint = () => {
    const styleEl = document.createElement('style')
    styleEl.id = '__cu-print-override'
    styleEl.textContent = '@page { size: letter portrait !important; margin: 0 !important; }'
    document.head.appendChild(styleEl)
    const orig = document.title
    document.title = `${data.header.registration || 'customer-update'}-status-${data.header.report_date || new Date().toISOString().slice(0, 10)}`
    document.body.setAttribute('data-printing', 'customer-update-export')
    setTimeout(() => {
      window.print()
      setTimeout(() => {
        document.title = orig
        document.body.removeAttribute('data-printing')
        document.getElementById('__cu-print-override')?.remove()
      }, 500)
    }, 50)
  }

  const usedDepts = new Set(data.dept_rows.map(r => r.dept))
  const availableDepts = orgDepts.filter(d => !usedDepts.has(d.key))
  const hasDepts = data.dept_rows.length > 0

  function EditableField({ label, value, onChange, type = 'text', compact = false }) {
    if (compact) {
      return (
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-gray-400 whitespace-nowrap">{label}:</span>
          {isEditor ? (
            <>
              <input
                type={type}
                value={value}
                onChange={e => onChange(e.target.value)}
                className={`text-xs text-gray-600 bg-transparent border-b border-gray-200 focus:outline-none focus:border-brand-red min-w-0 ${type === 'date' ? 'no-print' : ''}`}
              />
              {type === 'date' && (
                <span className="hidden print:block text-xs text-gray-600">{fmtDate(value) || '—'}</span>
              )}
            </>
          ) : (
            <span className="text-xs text-gray-600 truncate">{type === 'date' ? (fmtDate(value) || '—') : (value || '—')}</span>
          )}
        </div>
      )
    }
    return (
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">{label}</span>
        {isEditor ? (
          <>
            <input
              type={type}
              value={value}
              onChange={e => onChange(e.target.value)}
              className={`text-sm text-gray-900 font-medium bg-transparent border-b border-gray-200 focus:outline-none focus:border-brand-red min-w-0 ${type === 'date' ? 'no-print' : ''}`}
            />
            {type === 'date' && (
              <span className="hidden print:block text-sm text-gray-900 font-medium">{fmtDate(value) || '—'}</span>
            )}
          </>
        ) : (
          <span className="text-sm text-gray-900 font-medium truncate">{type === 'date' ? (fmtDate(value) || '—') : (value || '—')}</span>
        )}
      </div>
    )
  }

  return (
    <div id="customer-update-export" className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible">

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <img src={orgLogoUrl} alt={profile?.orgs?.name ?? 'Logo'} className="h-9 w-auto" onError={e => { e.currentTarget.src = '/logo.jpg' }} />
          <div className="w-px h-8 bg-gray-200" />
          <div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.4rem', letterSpacing: '0.06em', color: '#101820', lineHeight: 1 }}>
              {data.header.registration ? `${data.header.registration} Customer Update` : 'Customer Update'}
            </div>
            <div className="flex items-center gap-3 mt-1">
              <EditableField label="Work Order" value={data.header.work_order} onChange={v => setHeader('work_order', v)} compact />
              <span className="text-gray-200 text-xs">|</span>
              <EditableField label="As Of" value={data.header.report_date} onChange={v => setHeader('report_date', v)} type="date" compact />
            </div>
          </div>
        </div>
        {isEditor && (
          <button onClick={handlePrint} className="no-print flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print / PDF
          </button>
        )}
      </div>

      {/* ── Project Progress + Pipeline ──────────────────────────────────── */}
      {(hasDepts || isEditor) && (
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Project Progress</span>
            {isEditor ? (
              <div className="no-print flex items-center gap-2">
                {data.overall_pct_override !== null && data.overall_pct_override !== undefined ? (
                  <>
                    <input
                      type="number" min="0" max="100"
                      value={data.overall_pct_override}
                      onChange={e => setData(d => ({ ...d, overall_pct_override: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }))}
                      className="w-14 text-xs border border-gray-300 rounded-lg px-2 py-1 text-center focus:outline-none focus:border-brand-red"
                    />
                    <button onClick={() => setData(d => ({ ...d, overall_pct_override: null }))} className="text-xs text-brand-red hover:text-brand-red-dark">Auto</button>
                  </>
                ) : (
                  <>
                    <span className="text-sm font-bold" style={{ color: 'rgb(var(--brand-primary-rgb))' }}>{overallPct}% Complete</span>
                    <button onClick={() => setData(d => ({ ...d, overall_pct_override: overallPct }))} className="text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1">Override</button>
                  </>
                )}
              </div>
            ) : (
              <span className="text-sm font-bold" style={{ color: 'rgb(var(--brand-primary-rgb))' }}>
                {overallPct}% Complete
              </span>
            )}
          </div>
          <div className="w-full rounded-full overflow-hidden" style={{ height: 5, backgroundColor: '#f1f5f9', marginBottom: hasDepts ? 20 : 0 }}>
            <div style={{ height: '100%', width: `${overallPct}%`, backgroundColor: 'rgb(var(--brand-primary-rgb))', borderRadius: 9999, transition: 'width 0.4s ease' }} />
          </div>
          {hasDepts && (
            <Pipeline deptRows={data.dept_rows} orgDeptMap={orgDeptMap} allSubtasks={subtasks} arrivalDate={project?.arrival_date} departureDate={project?.departure_date} />
          )}
        </div>
      )}

      {/* ── Department Breakdown ─────────────────────────────────────────── */}
      <div className="px-6 py-4">
        {isEditor && availableDepts.length > 0 && (
          <div className="no-print flex flex-wrap gap-1.5 mb-4">
            {availableDepts.map(d => (
              <button
                key={d.key}
                onClick={() => addDeptRow(d.key)}
                className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-400 bg-white transition-colors font-medium"
                style={{ borderLeftColor: d.hex || '#6b7280', borderLeftWidth: 3 }}
              >
                + {d.label || d.key}
              </button>
            ))}
          </div>
        )}

        {!hasDepts ? (
          <div className="py-10 text-center text-sm text-gray-400">
            {isEditor ? 'Add departments above to build the progress timeline.' : 'No progress data configured.'}
          </div>
        ) : (
          data.dept_rows.map((row, i) => (
            <EditorDeptRow
              key={row.id}
              deptRow={row}
              orgDeptMap={orgDeptMap}
              index={i}
              total={data.dept_rows.length}
              allSubtasks={subtasks}
              onMove={moveDeptRow}
              onChange={u => updateDeptRow(i, u)}
              onDelete={() => deleteDeptRow(i)}
            />
          ))
        )}
      </div>

    </div>
  )
}
